import { Axios } from 'axios'
import { APIError } from './_axios'
import { Account, ActivityReport, Agency, Ipad, IpadSetupToken, LuluEstimatedCost, LuluShippingOption, PhysicalBook, SelectableObituary, Service, ServiceFamilyMember, ServiceSignature, TemplateCrop, User, UserAccountMembership } from './_models'
import { AccountFast, UserWithMembershipsFast } from './_models_fast';

// Using:
// /api/v1/app
// let's us simplify the resulting API URL.
// 

const localDevURL = 'http://localhost:4000/api/v1/app';
const localDevNgrokURL = 'https://signature-guestbook-api.ngrok.io';
const productionURL = 'https://api.serviceguestbook.com';

const localDevWSSURL = 'ws://localhost:4000';
const localDevNgrokWSSURL = 'wss://signature-guestbook-api.ngrok.io';
const productionWSSURL = 'wss://api.serviceguestbook.com';

export const BASE_API_URL = process.env.NODE_ENV === 'development' ? localDevNgrokURL : productionURL
export const BASE_WSS_URL = process.env.NODE_ENV === 'development' ? localDevNgrokWSSURL : productionWSSURL

// submit registration request:

export function submitRegistrationRequest(axios: Axios, data: any) {
    return axios.post(`/anon/auth/register-for-account`, data).then((res) => res.data).then((response: any) => {
        return true
    })
}

export function requestCodeForEmailAddress(axios: Axios, email: string) {
    return axios.post(`/anon/auth/request-code-for-email`, {
        email,
    })
}

export function confirmCodeForEmailAddress(axios: Axios, email: string, code: string): Promise<any> {
    return axios.post(`/anon/auth/confirm-code-for-email`, {
        email,
        code,
    }).then((res) => res.data).then((response: any) => {
        if (!response.data.api_token) {
            throw new APIError('Malformed response', {})
        }
        return response.data
    })
}

// Password-based authentication

export function loginWithPassword(axios: Axios, email: string, password: string, recaptchaToken: string | null): Promise<any> {
    return axios.post(`/anon/auth/login-with-password`, {
        email: email.trim().toLowerCase(),
        password,
        recaptcha_token: recaptchaToken,
    }).then((res) => res.data).then((response: any) => {
        if (!response.data.api_token) {
            throw new APIError('Malformed response', {})
        }
        return response.data
    })
}

export function requestPasswordReset(axios: Axios, email: string, recaptchaToken: string | null): Promise<any> {
    return axios.post(`/anon/auth/request-password-reset`, {
        email: email.trim().toLowerCase(),
        recaptcha_token: recaptchaToken,
    }).then((res) => res.data).then((response: any) => {
        return response.data
    })
}

export function resetPassword(axios: Axios, token: string, password: string, passwordConfirmation: string): Promise<any> {
    return axios.post(`/anon/auth/reset-password`, {
        token,
        password,
        password_confirmation: passwordConfirmation,
    }).then((res) => res.data).then((response: any) => {
        return response.data
    })
}

// Active service for account

export function getActiveService(axios: Axios) {
    return axios.get(`/authenticated/active-service`).then((res) => res.data).then((response: any) => {
        // response.data.service can be null, as there's not always an active service
        if (!response.data.service) {
            return null
        }
        return new Service(response.data.service)
    })
}

// Services

export function getServiceFamilyMembersByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.get(`/authenticated/services/${uniqueIdentifier}/family-members`).then((res) => res.data).then((response: any) => {
        if (!response.data.family_members) {
            throw new APIError('Malformed response', {})
        }
        return {
            familyMembers: response.data.family_members.map((familyMember: any) => new ServiceFamilyMember(familyMember)),
            familyMembersCount: response.data.family_members_count,
        }
    })
}

export function addFamilyMemberToServiceByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/authenticated/services/${uniqueIdentifier}/add-family-member`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.family_member) {
            throw new APIError('Malformed response', {})
        }
        return new ServiceFamilyMember(response.data.family_member)
    })
}

export function resendFamilyMemberInvitationByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, familyMemberUniqueIdentifier: string) {
    return axios.post(`/authenticated/services/${uniqueIdentifier}/family-members/${familyMemberUniqueIdentifier}/resend-invitation`, {}).then((res) => res.data).then((response: any) => {
        if (!response.data.family_member) {
            throw new APIError('Malformed response', {})
        }
        return new ServiceFamilyMember(response.data.family_member)
    })
}

export function createServiceQRCodePDF(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/authenticated/services/${uniqueIdentifier}/svg-to-png`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.resulting_pdf_url) {
            throw new APIError('Malformed response', {})
        }
        return response.data.resulting_pdf_url as string
    })
}

export function getAnonServiceByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    // Used for the public facing response
    return axios.get(`/anon/services/${uniqueIdentifier}`).then((res) => res.data).then((response: any) => {
        if (!response.data.service) {
            throw new APIError('Malformed response', {})
        }
        return new Service(response.data.service)
    })
}

export function signAnonServiceByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/anon/services/${uniqueIdentifier}/sign`, data).then((res) => res.data).then((response: any) => {
        return true; // simple, for now.
    })
}

export function retranscribeSignatureByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.post(`/authenticated/signatures/${uniqueIdentifier}/retranscribe`, {}).then((res) => res.data).then((response: any) => {
        if (!response.data.signature) {
            throw new APIError('Malformed response', {})
        }
        return new ServiceSignature(response.data.signature)
    })
}

export function updateSignatureTranscriptionByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/authenticated/signatures/${uniqueIdentifier}/update-transcription`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.signature) {
            throw new APIError('Malformed response', {})
        }
        return new ServiceSignature(response.data.signature)
    })
}

export function toggleSignatureLeaveOutOfPrintedMaterialsByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, leaveOutOfPrintedMaterials: boolean) {
    return axios.post(`/authenticated/signatures/${uniqueIdentifier}/update-leave-out-of-printed-materials`, {
        leave_out_of_printed_materials: leaveOutOfPrintedMaterials,
    }).then((res) => res.data).then((response: any) => {
        if (!response.data.signature) {
            throw new APIError('Malformed response', {})
        }
        return new ServiceSignature(response.data.signature)
    })
}


export function syncSignatureWithBridgeByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.post(`/authenticated/signatures/${uniqueIdentifier}/sync-with-bridge`, {}).then((res) => res.data).then((response: any) => {
        if (!response.data.signature) {
            throw new APIError('Malformed response', {})
        }
        return new ServiceSignature(response.data.signature)
    })
}

export function removeSignatureByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.post(`/authenticated/signatures/${uniqueIdentifier}/remove-signature`, {}).then((res) => res.data).then((response: any) => {
        return {
            success: true
        }
    })
}

export function endServiceByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.post(`/authenticated/services/${uniqueIdentifier}/end`, {}).then((res) => res.data).then((response: any) => {
        if (!response.data.service) {
            throw new APIError('Malformed response', {})
        }
        return new Service(response.data.service)
    })
}

export function startServiceByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.post(`/authenticated/services/${uniqueIdentifier}/start`, {}).then((res) => res.data).then((response: any) => {
        if (!response.data.service) {
            throw new APIError('Malformed response', {})
        }
        return new Service(response.data.service)
    })
}

export function getServiceSignaturesByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, page: number = 1, onlyPreneed: boolean = false, searchQuery: string = '') {
    const params = new URLSearchParams({
        page: page.toString(),
        onlyPreneed: onlyPreneed ? 'yes' : 'no',
        ...(searchQuery && { search: searchQuery })
    });
    
    return axios.get(`/authenticated/services/${uniqueIdentifier}/signatures?${params}`).then((res) => res.data).then((response: any) => {
        if (!response.data.signatures) {
            throw new APIError('Malformed response', {})
        }
        return {
            signatures: response.data.signatures.map((signature: any) => new ServiceSignature(signature)),
            signatures_count: response.data.signatures_count,
        }
    })
}


// Books

export function getBooks(axios: Axios) {
    return axios.get(`/authenticated/books`).then((res) => res.data).then((response: any) => {
        if (!response.data.books) {
            throw new APIError('Malformed response', {})
        }
        return response.data.books.map((book: any) => new PhysicalBook(book))
    })
}

export function createBook(axios: Axios, data: any) {
    return axios.post(`/authenticated/create-book`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.generated_book) {
            throw new APIError('Malformed response', {})
        }
        return new PhysicalBook(response.data.generated_book)
    })
}

export function createGeneratedPDF(axios: Axios, data: any) {
    return axios.post(`/authenticated/create-generated-pdf`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.generated_book) {
            throw new APIError('Malformed response', {})
        }
        return new PhysicalBook(response.data.generated_book)
    })
}

export function getBookByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.get(`/authenticated/books/${uniqueIdentifier}`).then((res) => res.data).then((response: any) => {
        if (!response.data.book) {
            throw new APIError('Malformed response', {})
        }
        return new PhysicalBook(response.data.book)
    })
}

export function getEstimatedLuluShippingOptions(axios: Axios, data: any) {
    return axios.post(`/authenticated/books/estimate-lulu-shipping-options`, data)
        .then((res) => res.data)
        .then((response: any) => {
            if (!response.data.options) {
                throw new APIError('Malformed response', {})
            }
            return response.data.options.map((option: any) => new LuluShippingOption(option))
        })
}

export function estimateLuluPrintJobCost(axios: Axios, data: any) {
    return axios.post(`/authenticated/books/estimate-lulu-print-job-cost`, data)
        .then((res) => res.data)
        .then((response: any) => {
            if (!response.data.estimated_cost) {
                throw new APIError('Malformed response', {})
            }
            return new LuluEstimatedCost(response.data.estimated_cost)
        })
}

export function createLuluPrintJob(axios: Axios, data: any) {
    return axios.post(`/authenticated/books/create-lulu-print-job`, data)
        .then((res) => res.data)
        .then((response: any) => {
            if (!response.data.lulu_print_job_id) {
                throw new APIError('Malformed response', {})
            }
            return response.data.lulu_print_job_id
        })
}

// Upload logo based on a service unique identifier
export function uploadLogoForAccount(axios: Axios, uniqueIdentifier: string, logoBlob: Blob) {
    const formData = new FormData();
    formData.append('funeral_home_logo', logoBlob);
    return axios.post(`/authenticated/accounts/${uniqueIdentifier}/upload-logo`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    }).then((res) => res.data).then((response: any) => {
        return true
    })
}

// iPads

export function getIpadByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.get(`/authenticated/ipads/${uniqueIdentifier}`).then((res) => res.data).then((response: any) => {
        if (!response.data.ipad) {
            throw new APIError('Malformed response', {})
        }
        return new Ipad(response.data.ipad)
    })
}

export function updateIpadByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/authenticated/ipads/${uniqueIdentifier}/update`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.ipad) {
            throw new APIError('Malformed response', {})
        }
        return new Ipad(response.data.ipad)
    })
}

export function removeServiceFromIpadByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.post(`/authenticated/ipads/${uniqueIdentifier}/remove-service`, {}).then((res) => res.data).then((response: any) => {
        if (!response.data.ipad) {
            throw new APIError('Malformed response', {})
        }
        return new Ipad(response.data.ipad)
    })
}

// Create an iPad Device QR code.

export function createIpadSetupToken(axios: Axios, ipadUniqueIdentifier: string) {
    return axios.post(`/authenticated/ipads/${ipadUniqueIdentifier}/create-setup-token`, {}).then((res) => res.data).then((response: any) => {
        if (!response.data.ipad_setup_token) {
            throw new APIError('Malformed response', {})
        }
        return new IpadSetupToken(response.data.ipad_setup_token)
    })
}

// Management

export function managementGetAccounts(axios: Axios) {
    return axios.get(`/authenticated/management/accounts`).then((res) => res.data).then((response: any) => {
        if (!response.data.accounts) {
            throw new APIError('Malformed response', {})
        }
        return {
            accounts: response.data.accounts.map((account: any) => new Account(account)),
            total_accounts: response.data.total_accounts,
        }
    })
}

export function managementCreateAccount(axios: Axios, data: any) {
    return axios.post(`/authenticated/management/create-account`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.account) {
            throw new APIError('Malformed response', {})
        }
        return new Account(response.data.account)
    })
}

export function managementUpdateAccountByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/authenticated/management/accounts/${uniqueIdentifier}/update`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.account) {
            throw new APIError('Malformed response', {})
        }
        return new Account(response.data.account)
    })
}

export function managementGetAccountUsersByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.get(`/authenticated/management/accounts/${uniqueIdentifier}/users`).then((res) => res.data).then((response: any) => {
        if (!response.data.user_account_memberships) {
            throw new APIError('Malformed response', {})
        }
        return {
            user_account_memberships: response.data.user_account_memberships.map((userAccountMembership: any) => new UserAccountMembership(userAccountMembership)),
            user_account_memberships_count: response.data.user_account_memberships_count,
        }
    })
}

export function managementGetAllUsers(axios: Axios) {
    return axios.get(`/authenticated/management/users`).then((res) => res.data).then((response: any) => {
        if (!response.data.users_with_memberships) {
            throw new APIError('Malformed response', {})
        }
        return {
            users_with_memberships: response.data.users_with_memberships.map((userWithMemberships: any) => new UserWithMembershipsFast(userWithMemberships)),
            users_with_memberships_count: response.data.users_with_memberships_count,
        }
    })
}

export function managementGetAccountByUniqueIdentifier(axios: Axios, uniqueIdentifier: string): Promise<Account> {
    return axios.get(`/authenticated/management/accounts/${uniqueIdentifier}`).then((res) => res.data).then((response: any) => {
        if (!response.data.account) {
            throw new APIError('Malformed response', {})
        }
        return new Account(response.data.account)
    })
}

export function managementGetAccountFastByUniqueIdentifier(axios: Axios, uniqueIdentifier: string): Promise<AccountFast> {
    return axios.get(`/authenticated/management/accounts/${uniqueIdentifier}?fast=yes`).then((res) => res.data).then((response: any) => {
        if (!response.data.account) {
            throw new APIError('Malformed response', {})
        }
        return new AccountFast(response.data.account)
    })
}

export function getCurrentAccountDetails(axios: Axios): Promise<Account> {
    return axios.get(`/authenticated/current-account/get-current-account`).then((res) => res.data).then((response: any) => {
        if (!response.data?.account) {
            throw new APIError('Malformed response', {})
        }
        return new Account(response.data.account)
    })
}

export function managementGenerateStripeBillingPortalForAccount(axios: Axios, accountUniqueIdentifier: string, data: any = {}): Promise<string> {
    return axios.post(`/authenticated/management/accounts/${accountUniqueIdentifier}/generate-stripe-billing-portal`, data)
        .then((res) => res.data)
        .then((response: any) => {
            if (!response.data.billing_portal_url) {
                throw new APIError('Malformed response', {})
            }
            return response.data.billing_portal_url
        })
}

export function managementCreateStripeCheckoutSessionForSpecificAccount(axios: Axios, accountUniqueIdentifier: string, data: { planId: string }): Promise<string> {
    return axios.post(`/authenticated/management/accounts/${accountUniqueIdentifier}/create-stripe-checkout-session`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.checkout_session_client_secret) {
            throw new APIError('Malformed response', {})
        }
        return response.data.checkout_session_client_secret
    })
}

export function managementAddUserToAccountByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/authenticated/management/accounts/${uniqueIdentifier}/add-user`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.user_account_membership) {
            throw new APIError('Malformed response', {})
        }
        return new UserAccountMembership(response.data.user_account_membership)
    })
}

export function managementGetUserByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.get(`/authenticated/management/users/${uniqueIdentifier}`).then((res) => res.data).then((response: any) => {
        if (!response.data.user) {
            throw new APIError('Malformed response', {})
        }
        return {
            user: new User(response.data.user),
            memberships: response.data.memberships.map((membership: any) => new UserAccountMembership(membership)),
        }
    })
}

export function managementUpdateUserByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/authenticated/management/users/${uniqueIdentifier}/update`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.user) {
            throw new APIError('Malformed response', {})
        }
        return {
            user: new User(response.data.user),
            memberships: response.data.memberships.map((membership: any) => new UserAccountMembership(membership)),
        }
    })
}

export function managementGetAllCrops(axios: Axios) {
    return axios.get(`/authenticated/management/crops`).then((res) => res.data).then((response: any) => {
        if (!response.data.crops) {
            throw new APIError('Malformed response', {})
        }
        return {
            crops: response.data.crops.map((crop: any) => new TemplateCrop(crop)),
            crops_count: response.data.crops_count,
        }
    })
}

export function managementGetCropByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.get(`/authenticated/management/crops/${uniqueIdentifier}`).then((res) => res.data).then((response: any) => {
        if (!response.data.crop) {
            throw new APIError('Malformed response', {})
        }
        return new TemplateCrop(response.data.crop)
    })
}

export function managementUpdateCropValuesByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/authenticated/management/crops/${uniqueIdentifier}/update-values`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.crop) {
            throw new APIError('Malformed response', {})
        }
        return new TemplateCrop(response.data.crop)
    })
}

export function managementGetCropSignatures(axios: Axios, uniqueIdentifier: string, page: number = 1, perPage: number = 20) {
    return axios.get(`/authenticated/management/crops/${uniqueIdentifier}/signatures`, {
        params: { page, per_page: perPage }
    }).then((res) => res.data).then((response: any) => {
        if (!response.data.signatures) {
            throw new APIError('Malformed response', {})
        }
        return {
            signatures: response.data.signatures.map((sig: any) => new ServiceSignature(sig)),
            totalCount: response.data.total_count,
            page: response.data.page,
            perPage: response.data.per_page,
            totalPages: response.data.total_pages
        }
    })
}

export function managementSetCropSelectedSignature(axios: Axios, uniqueIdentifier: string, signatureUniqueIdentifier: string | null) {
    return axios.post(`/authenticated/management/crops/${uniqueIdentifier}/set-selected-signature`, {
        signature_unique_identifier: signatureUniqueIdentifier
    }).then((res) => res.data).then((response: any) => {
        if (!response.data.crop) {
            throw new APIError('Malformed response', {})
        }
        return new TemplateCrop(response.data.crop)
    })
}

export function managementLinkFRMToBackend(axios: Axios, data: any) {
    return axios.post(`/authenticated/management/link-frm`, data).then((res) => res.data).then((response: any) => {
        if (typeof response.data.result !== 'object') {
            throw new APIError('Malformed response', {})
        }
        return response.data.result;
    })
}

// Families

export function familiesGetSignaturesForServiceByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, page: number = 1, invitationUniqueIdentifier: string) {
    return axios.get(`/anon/families/services/${uniqueIdentifier}/signatures?page=${page}&invitation_unique_identifier=${invitationUniqueIdentifier || ''}`).then((res) => res.data).then((response: any) => {
        if (!response.data.signatures) {
            throw new APIError('Malformed response', {})
        }
        return {
            signatures: response.data.signatures.map((signature: any) => new ServiceSignature(signature)),
            signatures_count: response.data.signatures_count,
        }
    })
}

// Admin routes

export function adminGetAgencies(axios: Axios) {
    return axios.get(`/authenticated/admin/agencies`).then((res) => res.data).then((response: any) => {
        if (!response.data.agencies) {
            throw new APIError('Malformed response', {})
        }
        return {
            agencies: response.data.agencies.map((agency: any) => new Agency(agency)),
            total_agencies: response.data.total_agencies,
        }
    })
}

export function adminCreateAgency(axios: Axios, data: any) {
    return axios.post(`/authenticated/admin/create-agency`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.agency) {
            throw new APIError('Malformed response', {})
        }
        return new Agency(response.data.agency)
    })
}

export function adminUpdateAgencyByUniqueIdentifier(axios: Axios, uniqueIdentifier: string, data: any) {
    return axios.post(`/authenticated/admin/agencies/${uniqueIdentifier}/update`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.agency) {
            throw new APIError('Malformed response', {})
        }
        return new Agency(response.data.agency)
    })
}

export function updateCurrentAgency(axios: Axios, data: any) {
    return axios.post(`/authenticated/current-agency/update-current-agency`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.agency) {
            throw new APIError('Malformed response', {})
        }
        return new Agency(response.data.agency)
    })
}
export function getCurrentAgency(axios: Axios) {
    return axios.get(`/authenticated/current-agency/get-current-agency`).then((res) => res.data).then((response: any) => {
        if (!response.data.agency) {
            throw new APIError('Malformed response', {})
        }
        return new Agency(response.data.agency)
    })
}

export function adminGetAgencyByUniqueIdentifier(axios: Axios, uniqueIdentifier: string) {
    return axios.get(`/authenticated/admin/agencies/${uniqueIdentifier}`).then((res) => res.data).then((response: any) => {
        if (!response.data.agency) {
            throw new APIError('Malformed response', {})
        }
        return new Agency(response.data.agency)
    })
}

export function adminUpdateAccountAdminFeatures(axios: Axios, data: any) {
    return axios.post(`/authenticated/admin/update-account-admin-features`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.account) {
            throw new APIError('Malformed response', {})
        }
        return new Account(response.data.account)
    })
}

export function adminGetGeneratedPDFs(axios: Axios, data: any): Promise<{ generated_pdfs: PhysicalBook[], total_generated_pdfs: number }> {
    return axios.post(`/authenticated/admin/generated-pdfs`, data).then((res) => res.data).then((response: any) => {
        if (!response.data.generated_pdfs) {
            throw new APIError('Malformed response', {})
        }
        return {
            generated_pdfs: response.data.generated_pdfs.map((pdf: any) => new PhysicalBook(pdf)),
            total_generated_pdfs: response.data.total_generated_pdfs,
        }
    })
}

// The Export Report on the management Accounts list: activity per month (YYYY-MM, from through to)
// for the accounts this user sees. Counted by the backend; see ActivityReport in _models.
export function managementGetActivityReport(axios: Axios, from: string, to: string): Promise<ActivityReport> {
    return axios.get(`/authenticated/management/activity-report`, { params: { from, to } }).then((res) => res.data).then((response: any) => {
        if (!response.data?.report?.months) {
            throw new APIError('Malformed response', {})
        }
        return response.data.report as ActivityReport
    })
}
